import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Printer } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useConcello, ConcelloLogo } from "@/lib/concellos";
import { CATEGORY, EVENT_TYPES } from "@/lib/constants";
import { SheetMap } from "@/components/plan/SheetMap";
import { SIGNS, SignMini, signQty } from "@/components/map/SignObjects";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { AVISO_DESFASADO } from "@/lib/estadosDesvio";
import { mandosDe } from "@/lib/staff";

const INSTR = {
  regulacion: "Corte total al tráfico rodado. Solo paso de emergencias y autorizados. Desviar según itinerario indicado. Reabrir solo tras paso del cierre de comitiva y con autorización del mando.",
  escolta: "Acompañar a la comitiva en apertura/cierre. Mantener comunicación con el mando e informar de la posición en los puntos de corte.",
  corte_estatico: "Custodia de vallas. No permitir el acceso de vehículos. Ante cualquier incidencia, avisar al agente de PL más próximo.",
  retirada: "Comprobar señalización R-308 colocada con antelación. Identificar vehículos estacionados, avisar a titulares y solicitar grúa municipal.",
  senalizacion: "Montar vallas, conos y señales según inventario. Verificar visibilidad de las señales de desvío antes de la hora de corte.",
  desmontaje: "Al finalizar el corte y con autorización del mando, retirar vallas, conos y señales, y comprobar que la vía queda abierta al tráfico.",
  habilitacion: "Habilitar provisionalmente la zona de tránsito especial (retirar bolardos/vallas), regular la entrada de vehículos desviados y cerrarla al finalizar el corte.",
};

const osm = (loc) => loc ? `https://www.openstreetmap.org/?mlat=${loc[0]}&mlon=${loc[1]}#map=18/${loc[0]}/${loc[1]}` : null;

const SignsTable = ({ ev, signs, unitId }) => {
  if (!signs.length) return null;
  const tasks = (ev.plan.tasks || []).filter((t) => t.element_ids);
  const tm = ev.plan.timings || {};
  return (
    <div className="mt-4" data-testid={`sheet-signs-${unitId}`}>
      <div className="text-[11px] font-semibold mb-1">Señalización y objetos del dispositivo <span className="font-normal text-slate-500">(resaltados los que le corresponde montar o retirar; se elige en la ficha de cada objeto o, si no, quien esté colocado al lado)</span></div>
      <table className="w-full text-[10px] border-collapse">
        <thead><tr>{["Objeto", "Ubicación", "Horario", "Monta", "Retira"].map((h) => <th key={h} className="text-left bg-slate-200 px-2 py-1 border border-slate-300">{h}</th>)}</tr></thead>
        <tbody>
          {signs.map((g) => {
            const mine = (cat) => tasks.filter((t) => t.category === cat && t.element_ids.includes(g.id)).flatMap((t) => t.assigned || []);
            const mon = mine("senalizacion");
            const ret = mine("desmontaje");
            const own = mon.includes(unitId) || ret.includes(unitId);
            return (
              <tr key={g.id} className={own ? "bg-amber-100 font-semibold" : ""}>
                <td className="px-2 py-1 border border-slate-300"><span className="inline-flex items-center gap-1.5"><SignMini type={g.sign_type} rot={g.rotation} size={14} />{SIGNS[g.sign_type]?.label}{signQty(g) > 1 ? ` x${signQty(g)}` : ""}</span></td>
                <td className="px-2 py-1 border border-slate-300">{ev.plan.ubicaciones?.[g.id] || (g.name || "").replace(/^[^·]*·\s*/, "") || "—"}</td>
                <td className="px-2 py-1 border border-slate-300 font-mono">{tm[g.id] ? `${tm[g.id].start_hm}–${tm[g.id].end_hm}` : "—"}</td>
                <td className="px-2 py-1 border border-slate-300 font-mono">{mon.join(", ") || "—"}</td>
                <td className="px-2 py-1 border border-slate-300 font-mono">{ret.join(", ") || "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const Sheet = ({ ev, u, first, signs, concello }) => (
  <div className={`bg-white text-slate-900 p-10 mb-6 shadow ${first ? "" : "print-break"}`} data-testid={`service-sheet-${u.id}`}>
    <div className="flex justify-between items-start gap-4 border-b-4 border-blue-800 pb-3">
      <ConcelloLogo ine={ev.concello} className="h-16 w-auto max-w-[6rem] shrink-0" />
      <div className="flex-1">
        <div className="text-[10px] tracking-[0.3em] font-mono text-blue-800">{concello || ev.town?.name ? `AYUNTAMIENTO DE ${(concello?.nombre || ev.town.name).toUpperCase()} · POLICÍA LOCAL` : "POLICÍAS DE GALICIA"}</div>
        <div className="font-display text-2xl font-extrabold mt-1">Hoja de servicio individual</div>
        <div className="text-sm font-semibold">{ev.name}</div>
        <div className="text-xs font-mono text-slate-600">{EVENT_TYPES[ev.type]} · {ev.date} · {ev.start_time}–{ev.end_time}</div>
        {mandosDe(ev).length > 0 && <div className="text-xs mt-1" data-testid="sheet-mandos"><b>Mando del dispositivo:</b> {mandosDe(ev).join(" · ")}</div>}
      </div>
      <div className="flex items-start gap-3">
        {ev.share_token && (
          <div className="text-center" data-testid={`sheet-qr-${u.id}`}>
            <QRCodeSVG value={`${window.location.origin}/m/${ev.share_token}/${u.id}`} size={84} level="M" />
            <div className="text-[8px] font-mono text-slate-500 mt-0.5">VISTA MÓVIL</div>
          </div>
        )}
      <div className="text-right border-2 border-slate-900 rounded px-4 py-2">
        <div className="text-[10px] font-mono text-slate-500">TIP</div>
        <div className="font-display text-3xl font-extrabold" data-testid={`sheet-tip-${u.id}`}>{u.tip || "________"}</div>
        <div className="font-mono text-xs">{u.id}</div>
      </div>
      </div>
    </div>
    <div className="grid grid-cols-3 gap-4 text-xs mt-4">
      {/* tipo de unidad y su indicativo (el de cada patrulla o la nota del grupo en «Personal»); el código interno (Z-01, PL-02…) solo como referencia */}
      <div><span className="text-slate-500">Unidad:</span> <b>{u.label.split(" · ")[0].replace(u.id, "").trim()}</b>{u.capacity > 1 ? ` (${u.capacity} agentes)` : ""} <span className="text-[10px] text-slate-400 font-mono">ref. {u.id}</span></div>
      <div><span className="text-slate-500">Turno:</span> <b className="font-mono">{u.shift_hm}</b></div>
      <div><span className="text-slate-500">Indicativo:</span> {u.indicativo || u.shift_label ? <b>{u.indicativo || u.shift_label}</b> : <span className="text-slate-400">— (se escribe en la pestaña «Personal»)</span>}</div>
    </div>
    {u.nota && <div className="text-xs mt-2" data-testid={`sheet-nota-${u.id}`}><span className="text-slate-500">Notas:</span> {u.nota}</div>}
    <SheetMap unit={u} route={ev.elements.find((e) => e.kind === "route")?.coords} signs={signs} staff={ev.plan.staff_points || []}
      ownSigns={(ev.plan.tasks || []).filter((t) => t.element_ids && (t.assigned || []).includes(u.id)).flatMap((t) => t.element_ids)} />
    <div className="text-[10px] text-slate-500 mt-1">Números = orden de los puestos · línea discontinua = traslado entre puestos · azul = recorrido del evento</div>
    <table className="w-full text-[11px] border-collapse mt-4">
      <thead><tr>{["Hora", "Puesto y órdenes", "Ubicación", "Desplazamiento"].map((h) => <th key={h} className="text-left bg-slate-200 px-2 py-1.5 border border-slate-300">{h}</th>)}</tr></thead>
      <tbody>
        {u.segments.map((s, i) => (
          <tr key={i} className="align-top">
            <td className="px-2 py-1.5 border border-slate-300 font-mono whitespace-nowrap"><span className="inline-block w-4 text-slate-500">{i + 1}.</span><b>{s.start_hm}</b>{s.category === "asignacion" ? <div className="text-[9px] text-slate-500 pl-4">hasta {s.end_hm}</div> : `–${s.end_hm}`}</td>
            <td className="px-2 py-1.5 border border-slate-300">
              <div className="font-semibold">{s.name}{s.handover ? " · RELEVO" : ""}</div>
              {s.category !== "asignacion" && <div className="text-slate-600">{CATEGORY[s.category]?.label}</div>}
              {s.notes ? <div className="mt-1 text-[11px] leading-snug border-l-4 border-blue-800 pl-2 whitespace-pre-wrap" data-testid={`sheet-notes-${u.id}-${i}`}><b>Órdenes:</b> {s.notes}</div>
                : INSTR[s.category] && <div className="text-slate-600 mt-1 text-[10px] leading-snug">{INSTR[s.category]}</div>}
            </td>
            <td className="px-2 py-1.5 border border-slate-300 font-mono text-[10px]">
              {s.loc ? <a href={osm(s.loc)} className="text-blue-700 underline">{s.loc[0].toFixed(5)}, {s.loc[1].toFixed(5)}</a> : "—"}
            </td>
            <td className="px-2 py-1.5 border border-slate-300">{s.depart_hm ? `Salir ${s.depart_hm} desde ${s.from} (${s.travel_min} min)` : "Presentarse desde base / Jefatura"}</td>
          </tr>
        ))}
      </tbody>
    </table>
    <SignsTable ev={ev} signs={signs} unitId={u.id} />
    <div className="text-[11px] text-slate-700 mt-4 border border-slate-300 rounded p-3">
      Comunicaciones: mantener la emisora en el canal asignado por el mando. Informar de inicio y fin de cada corte. Ante emergencias, priorizar el paso de servicios sanitarios y bomberos.
    </div>
    <div className="mt-10 grid grid-cols-2 gap-10 text-xs text-slate-600">
      <div className="border-t border-slate-400 pt-2">Recibí (TIP y firma)</div>
      <div className="border-t border-slate-400 pt-2">El/La Jefe/a del servicio</div>
    </div>
  </div>
);

export default function ServiceSheets() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const only = params.get("unit");
  const [ev, setEv] = useState(null);
  const [err, setErr] = useState(false);
  const { canEdit } = useAuth();
  useEffect(() => { api.get(`/events/${id}`).then((r) => setEv(r.data)).catch(() => setErr(true)); }, [id]);
  const concello = useConcello(ev?.concello);
  if (err) return <div className="p-10 bg-white min-h-screen text-slate-800" data-testid="sheets-error">Evento no encontrado.</div>;
  if (!ev) return <div className="p-10 text-slate-500">Cargando…</div>;
  if (!ev.plan) return <div className="p-10 bg-white min-h-screen text-slate-800">Genere primero el plan de tráfico.</div>;
  const signs = ev.elements.filter((e) => e.kind === "sign" && e.coords?.length);
  const units = ev.plan.units.filter((u) => u.segments.length && (!only || u.id === only));
  return (
    <div className="bg-slate-100 min-h-screen">
      <div className="no-print sticky top-0 z-[1000] bg-[#0b1329] text-white px-6 py-3 flex items-center justify-between">
        <span className="text-sm" data-testid="sheets-count">{units.length} hoja(s) de servicio · una por página al imprimir · el QR abre la vista móvil del agente</span>
        <div className="flex gap-2">
          {canEdit && (
            <button onClick={async () => {
              if (!window.confirm("Los QR/enlaces anteriores dejarán de funcionar. ¿Continuar?")) return;
              try {
                const r = await api.post(`/events/${id}/share/rotate`);
                setEv((p) => ({ ...p, share_token: r.data.share_token }));
                toast.success("Enlaces móviles regenerados");
              } catch (e) { toast.error(errMsg(e)); }
            }} className="px-3 py-2 rounded-lg text-sm border border-white/20 hover:bg-white/10" data-testid="rotate-links-button">Regenerar enlaces</button>
          )}
        <button onClick={() => window.print()} className="flex items-center gap-2 bg-sky-500 text-slate-950 font-semibold px-4 py-2 rounded-lg text-sm" data-testid="print-sheets-button">
          <Printer className="w-4 h-4" /> Imprimir / Guardar PDF
        </button>
        </div>
      </div>
      <div className="print-page max-w-[900px] mx-auto py-6" data-testid="service-sheets-page">
        {ev.plan_vigente === false && <div className="mb-4 border-2 border-red-600 bg-red-50 text-red-800 text-sm font-semibold rounded-lg px-4 py-3" data-testid="plan-stale-print">{AVISO_DESFASADO}</div>}
        {units.map((u, i) => <Sheet key={u.id} ev={ev} u={u} first={i === 0} signs={signs} concello={concello} staff={ev.plan.staff_points || []}
      ownSigns={(ev.plan.tasks || []).filter((t) => t.element_ids && (t.assigned || []).includes(u.id)).flatMap((t) => t.element_ids)} />)}
      </div>
    </div>
  );
}
